import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { toastHelper } from '../util/toastHelper.js'

const API_VERSION = 'v1'
const REQUEST_URL = `${process.env.REACT_APP_REQUEST_URL}/api/${API_VERSION}`

export const getNotifications = createAsyncThunk(
	'notification/getNotifications',
	async () => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/notification/get-notifications`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const countUnreadNotifications = createAsyncThunk(
	'notification/countUnreadNotifications',
	async () => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/notification/count-unread-notifications`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const getNotificationsKazoo = createAsyncThunk(
	'notification/getNotificationsKazoo',
	async (kazooId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/notification/get-notifications-kazoo/${kazooId}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const countUnreadNotificationsKazoo = createAsyncThunk(
	'notification/countUnreadNotificationsKazoo',
	async (kazooId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/notification/count-unread-notifications-kazoo/${kazooId}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const readNotification = createAsyncThunk(
	'notification/readNotification',
	async (notificationId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/notification/update-notification/${notificationId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						updates: {
							status: 'read',
						},
					}),
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const markAllNotificationsRead = createAsyncThunk(
	'notification/markAllNotificationsRead',
	async () => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/notification/mark-all-read`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
			return {
				success: false,
				message: err.message || 'Something went wrong',
			}
		}
	}
)


export const deleteNotification = createAsyncThunk(
	'notification/deleteNotification',
	async (notificationId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/notification/delete-notification/${notificationId}`,
				{
					method: 'DELETE',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const notifyUser = createAsyncThunk(
	'notification/notifyUser',
	async ({ receiverId, type, message }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/notification/notify-user`,
				{
					method: 'POST',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ receiverId, type, message }),
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
			return {
				success: false,
				message: err.message || 'Something went wrong',
			}
		}
	}
)

const initialState = {
	notifications: [],
	kazooNotifications: [],
	numUnreadNotifications: 0,
	numUnreadNotificationsKazoo: 0,
}

const notificationSlice = createSlice({
	name: 'notification',
	initialState,
	reducers: {
		updateCurrentUserNotifications: (state, { payload }) => {
			state.notifications = [payload, ...state.notifications]
			state.numUnreadNotifications += 1
		},
		appendCurrentUserNotifications: (state, { payload }) => {
			const list = Array.isArray(payload?.notifications)
				? payload.notifications
				: []
			if (list.length === 0) return
			state.notifications = [...list, ...state.notifications]
			state.numUnreadNotifications += list.length
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getNotifications.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to fetch notifications',
						'error'
					)
					return
				}
				state.notifications = payload.notifications.reverse()
			})
			.addCase(
				countUnreadNotifications.fulfilled,
				(state, { payload }) => {
					if (!payload || !payload.success) {
						return
					}
					state.numUnreadNotifications =
						payload.numUnreadNotifications
				}
			)
			.addCase(getNotificationsKazoo.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to fetch notifications',
						'error'
					)
					return
				}
				state.kazooNotifications = payload.notifications.reverse()
			})
			.addCase(
				countUnreadNotificationsKazoo.fulfilled,
				(state, { payload }) => {
					if (!payload || !payload.success) {
						return
					}
					state.numUnreadNotificationsKazoo =
						payload.numUnreadNotifications
				}
			)
			.addCase(readNotification.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					return
				}

				// user notifications
				state.notifications = state.notifications.map((notification) =>
					notification._id === payload.updatedNotification._id
						? payload.updatedNotification
						: notification
				)

				// kazoo notifications
				state.kazooNotifications = state.kazooNotifications.map(
					(notification) =>
						notification._id === payload.updatedNotification._id
							? payload.updatedNotification
							: notification
				)
			})
			.addCase(markAllNotificationsRead.fulfilled, (state, action) => {
				if (!action.payload?.success) {
					toastHelper(
						action.payload?.message || 'Failed to mark notifications read',
						'error'
					)
					return
				}
				const unreadIds = Array.isArray(action.meta.arg)
					? action.meta.arg
					: []
				const idsSet = new Set(unreadIds.map(String))
				state.notifications = state.notifications.map((n) =>
					idsSet.has(String(n._id)) ? { ...n, status: 'read' } : n
				)
				const updatedCount = action.payload.updatedCount ?? 0
				state.numUnreadNotifications = Math.max(
					0,
					state.numUnreadNotifications - updatedCount
				)
				toastHelper(
					`${updatedCount} notifications marked as read`,
					'success'
				)
			})
			.addCase(deleteNotification.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					return
				}
				// user notifications
				state.notifications = state.notifications.filter(
					(notification) =>
						notification._id !== payload.notificationId
				)

				// kazoo notifications
				state.kazooNotifications = state.kazooNotifications.filter(
					(notification) =>
						notification._id !== payload.notificationId
				)
			})
	},
})

export const {
	updateCurrentUserNotifications,
	appendCurrentUserNotifications,
} = notificationSlice.actions
export default notificationSlice.reducer
